import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Clapperboard } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/AuthContext";

export const Logo = ({ compact = false }) => (
  <span className="group flex items-center gap-2.5">
    <span
      aria-hidden="true"
      className="relative grid h-8 w-8 place-items-center rounded-[10px] bg-gradient-to-br from-[#ff4b2b] to-[#f08a1e] shadow-[0_0_20px_-4px_rgba(255,75,43,0.8)] transition-transform duration-300 group-hover:scale-105"
    >
      <Clapperboard className="h-4 w-4 text-white" strokeWidth={2.4} />
    </span>
    {compact ? (
      <span className="sr-only">Studio Movies IA</span>
    ) : (
      <span className="hidden font-display text-[15px] font-semibold tracking-tight sm:inline">
        STUDIO MOVIES <span className="text-magma">IA</span>
      </span>
    )}
  </span>
);

export default function SiteNav() {
  const { t, locale, setLocale } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const L = (p) => `/${locale}${p}`;

  const links = [
    [t("nav.product"), L("/features")],
    [t("nav.templates"), L("/templates")],
    [t("nav.pricing"), L("/pricing")],
    [t("nav.blog"), L("/blog")],
    [t("nav.enterprise"), L("/enterprise")],
  ];

  const swap = () => {
    const next = locale === "en" ? "fr" : "en";
    setLocale(next);
    const p = window.location.pathname.replace(/^\/(en|fr)/, `/${next}`);
    nav(p.startsWith(`/${next}`) ? p : `/${next}`);
  };

  return (
    <header className="glass fixed inset-x-0 top-0 z-50 border-b border-white/[0.07]">
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-6 lg:px-10">
        <Link
          to={L("")}
          data-testid="nav-logo"
          aria-label={locale === "fr" ? "Studio Movies IA — accueil" : "Studio Movies IA — home"}
          className="shrink-0"
        >
          <Logo />
        </Link>

        <nav className="hidden items-center gap-9 lg:flex">
          {links.map(([label, to]) => (
            <Link
              key={to}
              to={to}
              data-testid={`nav-link-${label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-[13.5px] text-[#9194a6] transition-colors duration-200 hover:text-white"
            >
              {label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={swap}
            data-testid="locale-toggle"
            className="mono rounded-full border border-[#2a2c3c] px-2.5 py-1 text-[11px] uppercase text-[#9194a6] transition-colors duration-200 hover:border-[#6a7090] hover:text-white"
          >
            {locale === "en" ? "FR" : "EN"}
          </button>
          {user ? (
            <Link
              to="/studio"
              data-testid="nav-open-studio"
              className="btn-primary hidden rounded-full px-5 py-2 text-[13px] font-semibold sm:block"
            >
              {t("nav.studio")}
            </Link>
          ) : (
            <>
              <Link
                to={L("/login")}
                data-testid="nav-login"
                className="hidden text-[13.5px] text-[#9194a6] transition-colors hover:text-white sm:block"
              >
                {t("nav.login")}
              </Link>
              <Link
                to={L("/register")}
                data-testid="nav-register"
                className="btn-primary rounded-full px-5 py-2 text-[13px] font-semibold"
              >
                {t("nav.start")}
              </Link>
            </>
          )}
          <button
            className="lg:hidden"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/[0.07] bg-[#08080d] px-6 py-5 lg:hidden">
          {links.map(([label, to]) => (
            <Link
              key={to}
              to={to}
              onClick={() => setOpen(false)}
              className="block py-2.5 text-[15px] text-[#d3d5df]"
            >
              {label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}
